<div>
  <h4 class="header-title">{{ tab_name }} : Innovation Activity</h4>
  <p class="header-subtitle">
    This graph displays the innovation score and various innovation indicators of {{ tab_name }} over the last 10 years.
  </p>

  <div class="d-flex justify-content-center my-4" *ngIf="showScoreSpinner">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <div class="alert alert-danger" *ngIf="scoreLoadingFailed">
    Failed to load innovation score history. Please try again or contact
    <a href="mailto:info@quant-ip.com">info&#64;quant-ip.com</a>.
  </div>

  <div class="empty-state" *ngIf="scoreDataMissing && !showScoreSpinner">
    <i class="bi bi-search empty-state-icon"></i>
    <h5>No Innovation Score History Available</h5>
    <p>There haven't been any inventions published in the last 10 years to display innovation score history.</p>
  </div>

  <ng-container *ngIf="!scoreDataMissing">
    <div #exportTarget class="chart-card">
      <div class="card-header-row">
        <h5 class="chart-title">{{ scoreChartTitle }}</h5>
        <div class="chart-toolbar" data-html2canvas-ignore="true" *ngIf="!printMode">
          <button type="button" class="tool-btn" (click)="resetZoom()" title="Reset zoom">
            <i class="bi bi-arrow-counterclockwise"></i>
          </button>
          <button type="button" class="tool-btn" (click)="downloadPng()" title="Download PNG">
            <i class="bi bi-download"></i>
          </button>
        </div>
      </div>

      <div class="chip-row" *ngIf="!printMode">
        <div class="metric-chips">
          <button type="button" class="metric-chip" [class.active]="timeRange === '10y'" (click)="setTimeRange('10y')">
            <span class="chip-dot" *ngIf="timeRange === '10y'" style="background:#475569"></span>
            10Y
          </button>
          <button type="button" class="metric-chip" [class.active]="timeRange === '3y'" (click)="setTimeRange('3y')">
            <span class="chip-dot" *ngIf="timeRange === '3y'" style="background:#475569"></span>
            3Y
          </button>
          <button type="button" class="metric-chip" [class.active]="timeRange === '1y'" (click)="setTimeRange('1y')">
            <span class="chip-dot" *ngIf="timeRange === '1y'" style="background:#475569"></span>
            1Y
          </button>
        </div>

        <div class="metric-chips">
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'abs_growth'"
            (click)="toggleScoreSecondaryAxis('abs_growth')"
            matTooltip="Number of inventions published in the last 12 months" matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'abs_growth'"
              [style.background]="SECONDARY_CFG.abs_growth.color"></span>
            <i class="bi bi-bar-chart-steps"></i> Abs. Growth
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'growth'"
            (click)="toggleScoreSecondaryAxis('growth')"
            matTooltip="% Year-over-year ratio. Inventions filed in the last 12 months divided by the prior 12 months"
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'growth'"
              [style.background]="SECONDARY_CFG.growth.color"></span>
            <i class="bi bi-graph-up-arrow"></i> Rel. Growth
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'size'"
            (click)="toggleScoreSecondaryAxis('size')"
            matTooltip="Number of inventions published in the last 120 months" matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'size'"
              [style.background]="SECONDARY_CFG.size.color"></span>
            <i class="bi bi-bar-chart"></i> Size
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'quality'"
            (click)="toggleScoreSecondaryAxis('quality')"
            matTooltip="Average quality score of inventions published in the last 12 months" matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'quality'"
              [style.background]="SECONDARY_CFG.quality.color"></span>
            <i class="bi bi-award"></i> Quality
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'rank'"
            (click)="toggleScoreSecondaryAxis('rank')"
            matTooltip="Percentile Rank based on Innovation Score (100 = Best in peer group)"
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'rank'"
              [style.background]="SECONDARY_CFG.rank.color"></span>
            <i class="bi bi-trophy"></i> Percentile Rank
          </button>
        </div>
      </div>

      <p class="zoom-hint" data-html2canvas-ignore="true" *ngIf="!printMode">Drag to zoom • Shift + drag to pan  •
        Double-click to reset</p>

      <div class="chart-container" *ngIf="showScoreChart">
        <canvas baseChart [type]="'bar'" [data]="chartjsData" [options]="chartjsOptions" [plugins]="chartPlugins"
          (dblclick)="resetZoom()">
        </canvas>
      </div>

      <div class="chart-legend" *ngIf="showScoreChart">
        <span class="legend-item">
          <span class="legend-line legend-line--score"></span> Innovation Score
        </span>
        <ng-container *ngIf="secondaryAxis">
          <span class="legend-item" *ngIf="SECONDARY_CFG[secondaryAxis].type === 'bar'">
            <span class="legend-bar" [style.background]="SECONDARY_CFG[secondaryAxis].color"></span>
            {{ SECONDARY_CFG[secondaryAxis].label }}
          </span>
          <span class="legend-item" *ngIf="SECONDARY_CFG[secondaryAxis].type === 'scatter'">
            <span class="legend-line" [style.background]="SECONDARY_CFG[secondaryAxis].color"></span>
            {{ SECONDARY_CFG[secondaryAxis].label }}
          </span>
        </ng-container>
        <span class="legend-item legend-item--threshold">
          <span class="legend-threshold" [style.border-top-color]="SIGNAL.BUY"></span> BUY · 75
        </span>
        <span class="legend-item legend-item--threshold">
          <span class="legend-threshold" [style.border-top-color]="SIGNAL.SELL"></span> SELL · 25
        </span>
      </div>
    </div>

    <!-- <div class="keywords-card" *ngIf="!keywordsError || !showKeywordsNoData">
      <h5>Top Innovation Keywords</h5>

      <div class="d-flex justify-content-center my-3" *ngIf="keywordsLoading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div class="alert alert-danger py-2" *ngIf="keywordsError">
        Failed to load top keywords. Please try again later.
      </div>

      <div class="empty-state" *ngIf="showKeywordsNoData && !keywordsLoading && !keywordsError">
        <i class="bi bi-card-text empty-state-icon"></i>
        <h6>No Keywords Available</h6>
        <p class="mb-0">We could not find enough patent text data to extract meaningful keywords.</p>
      </div>

      <div *ngIf="!keywordsLoading && !keywordsError && !showKeywordsNoData">
        <table class="table mb-0 keywords-table">
          <thead>
            <tr>
              <th scope="col">Keyword</th>
              <th scope="col" class="text-end">Inventions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let kw of keywords | slice:0:10">
              <td>{{ kw.ti_kw }}</td>
              <td class="text-end">{{ kw.invention_count }}</td>
            </tr>
          </tbody>
        </table>
        <p class="keywords-caption mt-2 mb-0">
          Showing up to 10 most frequent keywords linked to this company and theme.
        </p>
      </div>
    </div> -->
  </ng-container>


  <div style="font-size: 10px;margin-top:20px; margin-bottom:10px;">
        <span style="font-size: 10px;margin-top:20px; margin-bottom:10px;">
        Important Notice: 
This content is provided for general informational purposes only. It reflects a quantitative assessment of a company’s innovation activity based primarily on patent-related data and does not constitute investment advice, investment brokerage, portfolio management, a personal recommendation, or an offer or solicitation to buy, sell, or hold any security. The rating shown on this page, including any “BUY”, “HOLD”, or “SELL” label, is generated by a model using patent and related innovation signals only. It does not take into account any investor’s individual investment objectives, financial situation, tax position, liquidity needs, risk tolerance, existing holdings, or suitability requirements. The model does not incorporate all factors relevant to an investment decision, including valuation, financial performance, balance sheet strength, macroeconomic conditions, governance, litigation, competitive dynamics, or market sentiment. This content must not be used as the sole basis for any investment decision. 
     </span>
      </div>
</div>